import type {
	CustomerQuoteBoardItem,
	QuoteBoardItem,
} from "~/shared/schemas/quote-board-schema.ts";

import {
	formatCurrency,
	formatDateString,
} from "../utils/quote-board-data-transformers.ts";
import type { QuoteBoardCarrierAction } from "../utils/quote-board-status.ts";
import { getQuoteBoardCarrierAction } from "../utils/quote-board-status.ts";

type BoardItem = QuoteBoardItem | CustomerQuoteBoardItem;

export type QuoteBoardCardFields = {
	quoteId: number;
	poNumber: string;
	shipDate: string;
	originAddress: string;
	destinationAddress: string;
	createdDate: string;
	weight: string;
	carrierName: string | null;
	carrierAction: QuoteBoardCarrierAction;
	maxLiability: string;
	rate: string;
	customerReference: string;
	submittedBy: string;
	bulkQuoteId: string | null;
};

const EM_DASH = "\u2014";

/** True when a preformatted currency string (e.g. "$0/$0") has no non-zero amount. */
const isBlankOrZeroAmount = (value: string | null | undefined): boolean => {
	if (!value?.trim()) return true;
	const amounts = value.match(/\d+(?:\.\d+)?/g);
	return !amounts || amounts.every((amount) => Number.parseFloat(amount) === 0);
};

/**
 * Normalizes the internal/external Quote Board row shapes into the flat set
 * of display fields the mobile card needs, mirroring the field mapping in
 * `board-columns.tsx` (the grid's column definitions) so the two views never
 * drift on formatting.
 */
export const getQuoteBoardCardFields = (
	item: BoardItem,
	onOpenBulkCarrierSelection?: (quoteId: number) => void,
): QuoteBoardCardFields => {
	const poNumber =
		"poNumbers" in item
			? (item.poNumbers ?? EM_DASH)
			: (item.poNumber?.toString() ?? EM_DASH);
	const submittedBy =
		"createdByEmail" in item
			? item.createdByEmail
			: (item.createdByName ?? EM_DASH);
	// A missing, blank, or zero rate is "no rate set" and reads as an em dash
	// rather than a misleading $0.00.
	const customerRate = "customerRate" in item ? item.customerRate : null;
	const hasRateValue =
		item.selectedCarrierPriceId != null &&
		customerRate != null &&
		String(customerRate).trim() !== "" &&
		customerRate !== 0;
	const rate = hasRateValue ? formatCurrency(customerRate) : EM_DASH;

	const carrierAction = onOpenBulkCarrierSelection
		? getQuoteBoardCarrierAction(item)
		: "none";

	return {
		quoteId: item.quoteId,
		poNumber,
		shipDate: formatDateString(item.shipmentDate) ?? EM_DASH,
		originAddress: item.originAddress ?? EM_DASH,
		destinationAddress: item.destinationAddress ?? EM_DASH,
		createdDate: formatDateString(item.createdDate) ?? EM_DASH,
		weight: `${item.totalWeight ?? 0} lbs`,
		carrierName: item.carrierName ?? null,
		carrierAction,
		maxLiability: isBlankOrZeroAmount(item.maxLiability)
			? EM_DASH
			: (item.maxLiability ?? EM_DASH),
		rate,
		customerReference: item.customerReferences?.trim() || "N/A",
		submittedBy,
		bulkQuoteId: item.bulkQuoteId,
	};
};
